import React, {useEffect, useState} from 'react'
import clinicService from '../../../../../services/clinic.service';
import {useParams} from 'react-router-dom';
import {Pagination, Table, Spinner, Alert, Badge, Button, Collapse} from 'react-bootstrap';
import Loading from "../../../../common/Loading";


function HistoryTab() {
    const {clinicId} = useParams();
    const [historyList, setHistoryList] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [copiedItem, setCopiedItem] = useState(null);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Available page sizes
    const pageSizeOptions = [10, 20, 50];

    useEffect(() => {
        const getHistory = async () => {
            try {
                setLoading(true);
                setError(null);

                // Calculate page number for API (0-based)
                const pageNumber = currentPage - 1;

                const res = await clinicService.getHistory(clinicId, pageNumber, itemsPerPage);

                setHistoryList(res.data.content);
                setTotalElements(res.data.totalElements);
            } catch (err) {
                console.error(err);
                setError('Failed to load history data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        getHistory();
    }, [clinicId, currentPage, itemsPerPage]);

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedItem({id, type: 'id'});
        setTimeout(() => setCopiedItem(null), 2000);
    };

    // Toggle row expansion
    const toggleRowExpand = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Handle items per page change
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); // Reset to first page when changing page size
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalElements / itemsPerPage);

    // Generate pagination items
    let paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item
                key={number}
                active={number === currentPage}
                onClick={() => setCurrentPage(number)}
            >
                {number}
            </Pagination.Item>
        );
    }

    return (
        <div className="card card-padding shadow-none">
            <div className="card-body">
                <div className="card-title">
                    <i className="bi bi-clock"></i> Sync History
                </div>

                {error && (
                    <Alert variant="danger" className="mt-3">
                        {error}
                    </Alert>
                )}

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <span className="me-2">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{width: '70px'}}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {pageSizeOptions.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        <span className="ms-2">entries</span>
                    </div>
                    <div>
                        Total records: <strong>{totalElements}</strong>
                    </div>
                </div>

                {loading ? (
                    <Loading/>
                ) : (
                    <>
                        <div className="card-padding shadow-none table-responsive">
                            <table className="table table-bordered table-hover mb-0">
                                <thead className="table-light">
                                <tr>
                                    <th className="col-4"><i className="bi bi-journal-medical"></i> ID</th>
                                    <th className="col-2"><i className="bi bi-cloud-check"></i> Result</th>
                                    <th className="col-4"><i className="bi bi-clock-history"></i> Updated</th>
                                    <th className="col-2"><i className="bi bi-gear"></i> Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {historyList.length > 0 ? (
                                    historyList.map((item) => (
                                        <React.Fragment key={item.id}>
                                            <tr>
                                                <td className="text-secondary small">{item.id}</td>
                                                <td>
                                                    {item.result === 'SUCCESS' ? (
                                                        <Badge bg="success">Success</Badge>
                                                    ) : (
                                                        <Badge bg="danger">Error</Badge>
                                                    )}
                                                </td>
                                                <td>{item.createdAt}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => toggleRowExpand(item.id)}
                                                        aria-controls={`collapse-notes-${item.id}`}
                                                        aria-expanded={expandedRows[item.id]}
                                                    >
                                                        {expandedRows[item.id] ? <i className="bi bi-chevron-up"></i> :
                                                            <i className="bi bi-chevron-down"></i>}
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan="4" className="p-0">
                                                    <Collapse in={expandedRows[item.id]}>
                                                        <div id={`collapse-notes-${item.id}`} className="p-3 bg-light">
                                                            <div className="mb-2 d-flex align-items-center">
                                                                <strong className="me-2">ID:</strong>
                                                                <span
                                                                    className="small font-monospace">{item.id} &nbsp;</span>
                                                                <button
                                                                    className="btn btn-sm btn-outline-secondary py-0 px-1"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        copyToClipboard(item.id, item.id);
                                                                    }}
                                                                    title="Copy ID"
                                                                >
                                                                    {copiedItem?.id === item.id && copiedItem?.type === 'id' ? (
                                                                        <i className="bi bi-check2"></i>
                                                                    ) : (
                                                                        <i className="bi bi-copy"></i>
                                                                    )}
                                                                </button>
                                                            </div>
                                                            <div className="mb-2">
                                                                <strong>PMS Type:</strong> {item.pmsType}
                                                            </div>
                                                            <div>
                                                                <strong>Notes:</strong> <br/>
                                                                <div className="card-border card-padding">
                                                                    <code> {item.notes} </code>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Collapse>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center">
                                            No history records found
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex justify-content-between mt-3">
                            <div>
                                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                                {Math.min(currentPage * itemsPerPage, totalElements)} of {totalElements} entries
                            </div>

                            <Pagination>
                                <Pagination.First
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                    disabled={currentPage === 1}
                                />

                                {paginationItems}

                                <Pagination.Next
                                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default HistoryTab;